import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, formatPrice, TIMEPAYMENT_URL } from '../lib/api';
import { Filter, DollarSign, Truck, ShieldCheck, Headphones } from 'lucide-react';
import { StockBadge, PrimaryShopAction } from '../components/ShopActions';
import { hasPrice, isBuyNow } from '../lib/shop';
import useSEO from '../hooks/useSEO';

const types = [
    { key: 'all', label: 'All Equipment' },
    { key: 'machine', label: 'Machines' },
    { key: 'accessory', label: 'Accessories & Parts' },
];

const categories = [
    { key: 'all', label: 'All Categories' },
    { key: 'dry-ice', label: 'Dry Ice Cleaners' },
    { key: 'detailing-light-industrial', label: 'Detailing / Light' },
    { key: 'industrial-performance', label: 'Industrial Performance' },
    { key: 'sandblasting', label: 'Sandblasting' },
    { key: 'used', label: 'Used Machines' },
    { key: 'accessories', label: 'Accessories' },
    { key: 'trailer', label: 'Trailers' },
    { key: 'air-compressor', label: 'Air Compressors' },
];

const perks = [
    { icon: Truck, label: 'Nationwide shipping' },
    { icon: ShieldCheck, label: 'Authorized dealer warranty' },
    { icon: Headphones, label: 'Training & support included' },
];

const Machines = () => {
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [params, setParams] = useSearchParams();
    const type = params.get('type') || 'all';
    const cat = params.get('category') || 'all';

    useSEO({
        title: 'Shop Dry Ice Blasting Machines, Accessories & Parts',
        description: 'Buy dry ice blasting machines, nozzles, hoses and accessories from an authorized dealer. Buy accessories online, request quotes on machines, financing available.',
    });

    useEffect(() => {
        setLoading(true);
        api.get('/machines').then((r) => setMachines(r.data)).finally(() => setLoading(false));
    }, []);

    const setFilter = (k, v) => {
        const next = new URLSearchParams(params);
        if (v === 'all') next.delete(k); else next.set(k, v);
        setParams(next, { replace: true });
    };

    const filtered = useMemo(() => machines.filter((m) =>
        (type === 'all' || m.machine_type === type) && (cat === 'all' || m.category === cat)
    ), [type, cat, machines]);

    const usedCats = categories.filter((c) => c.key === 'all' || machines.some((m) => m.category === c.key));

    return (
        <div data-testid="machines-page">
            <section className="border-b border-neutral-200 bg-neutral-50">
                <div className="container-page pt-32 pb-14">
                    <div className="label-caps text-brand-primary mb-6">/ Equipment shop</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl text-neutral-900">
                        Shop dry ice<br />equipment
                    </h1>
                    <p className="mt-6 text-neutral-600 max-w-2xl">
                        Anything with a listed price can be bought online — add it to your cart and pay by PayPal,
                        invoice (card or ACH) or bank wire. Machines without a listed price get a current price from our team.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-6">
                        {perks.map(({ icon: Icon, label }) => (
                            <div key={label} className="flex items-center gap-2 text-sm text-neutral-700">
                                <Icon className="h-4 w-4 text-brand-primary" /> {label}
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-wrap gap-2" data-testid="shop-type-tabs">
                        {types.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setFilter('type', t.key)}
                                className={`px-5 py-3 label-caps border transition-colors ${type === t.key ? 'bg-neutral-900 border-neutral-900 text-on-dark' : 'border-neutral-300 text-neutral-700 hover:border-neutral-900 bg-white'}`}
                                data-testid={`shop-type-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container-page py-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <aside className="lg:col-span-3">
                        <div className="lg:sticky lg:top-28">
                            <div className="flex items-center gap-2 label-caps text-neutral-500 mb-4">
                                <Filter className="h-4 w-4" /> Category
                            </div>
                            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible">
                                {usedCats.map((c) => (
                                    <button
                                        key={c.key}
                                        onClick={() => setFilter('category', c.key)}
                                        className={`text-left px-4 py-3 label-caps border transition-colors whitespace-nowrap ${
                                            cat === c.key ? 'border-brand-primary bg-brand-primary/10 text-neutral-900' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'
                                        }`}
                                        data-testid={`filter-${c.key}`}
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>

                            <div className="hidden lg:block mt-10 border border-neutral-200 p-6">
                                <div className="label-caps text-brand-primary mb-3">Financing</div>
                                <p className="text-sm text-neutral-600 mb-4">
                                    Flexible monthly payments on new and used machines through TimePayment.
                                </p>
                                <a href={TIMEPAYMENT_URL} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-2 border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-colors px-4 py-2 label-caps"
                                    data-testid="finance-sidebar-cta">
                                    <DollarSign className="h-4 w-4" /> Apply for financing
                                </a>
                            </div>
                        </div>
                    </aside>

                    <main className="lg:col-span-9">
                        <div className="label-caps text-neutral-500 mb-4" data-testid="shop-result-count">{loading ? 'Loading…' : `${filtered.length} item${filtered.length === 1 ? '' : 's'}`}</div>
                        {!loading && filtered.length === 0 ? (
                            <div className="text-neutral-500 label-caps" data-testid="machines-empty">No equipment matches this filter.</div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="machines-grid">
                                {filtered.map((m) => <MachineCard key={m.id} m={m} />)}
                            </div>
                        )}
                    </main>
                </div>
            </section>
        </div>
    );
};

export const MachineCard = ({ m }) => (
    <div className="group border border-neutral-200 bg-white hover:border-brand-primary hover:shadow-lg transition-[border-color,box-shadow] flex flex-col" data-testid={`machine-card-${m.id}`}>
        <Link to={`/machines/${m.slug || m.id}`} className="relative block aspect-square bg-neutral-50 overflow-hidden">
            {m.image_url ? (
                <img src={m.image_url} alt={m.name} loading="lazy" decoding="async" className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-700" />
            ) : null}
            <span className={`absolute top-3 left-3 label-caps text-[10px] px-2 py-1 ${isBuyNow(m) ? 'bg-brand-primary text-white' : 'bg-neutral-900 text-on-dark'}`} data-testid={`machine-mode-${m.id}`}>
                {isBuyNow(m) ? 'Buy Now' : 'Get Price'}
            </span>
        </Link>
        <div className="p-6 flex-1 flex flex-col">
            <div className="label-caps text-neutral-400 mb-2 text-xs">{m.category.replace(/-/g, ' ')}</div>
            <Link to={`/machines/${m.slug || m.id}`}>
                <h3 className="font-display font-black text-xl uppercase tracking-tight text-neutral-900 hover:text-brand-primary transition-colors">{m.name}</h3>
            </Link>
            <p className="text-neutral-600 text-sm mt-2 line-clamp-2">{m.short_description}</p>
            <div className="mt-4 flex items-end justify-between gap-2 flex-wrap">
                {hasPrice(m) ? (
                    <div className="font-display font-black text-2xl text-neutral-900" data-testid={`machine-price-${m.id}`}>
                        {!isBuyNow(m) && <span className="block label-caps text-[10px] text-neutral-500 font-normal">Starting at</span>}
                        {formatPrice(m.price)}
                    </div>
                ) : (
                    <span className="label-caps text-neutral-600 border border-neutral-300 px-2 py-1 text-xs" data-testid={`machine-price-${m.id}`}>Call for current price</span>
                )}
                {isBuyNow(m) && <StockBadge m={m} />}
            </div>
            <div className="mt-auto pt-5">
                <PrimaryShopAction m={m} className="w-full px-4 py-3 text-sm" testid={`machine-primary-${m.id}`} />
            </div>
        </div>
    </div>
);

export default Machines;
